import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PIPELINE_METRICS, GET_DEALS_REQUIRING_ATTENTION } from '../../graphql/sales-deals';
import { GET_SERVICE_STATISTICS } from '../../graphql/professional-services';
import { GET_UNPAID_STATEMENTS_SUMMARY } from '../../graphql/sales-commission-statements';

const STATUS_PILLS = [
    { key: 'ALL', label: 'All' },
    { key: 'PENDING', label: 'Pending' },
    { key: 'CONCLUDED', label: 'Active (Concluded)' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'FAILED', label: 'Failed' }
];

const SalesPipelineDashboard = () => {
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0], // Jan 1 of previous year
        endDate: new Date().toISOString().split('T')[0] // Today
    });
    const [selectedStatus, setSelectedStatus] = useState('ALL');

    // Pipeline metrics query
    const { loading: loadingMetrics, data: metricsData, refetch: refetchMetrics } = useQuery(GET_PIPELINE_METRICS, {
        variables: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            status: selectedStatus === 'ALL' ? null : selectedStatus
        }
    });

    // Deals requiring attention
    const { loading: loadingAttention, data: attentionData } = useQuery(GET_DEALS_REQUIRING_ATTENTION);

    // Professional services statistics
    const { loading: loadingServices, data: servicesData } = useQuery(GET_SERVICE_STATISTICS);

    // Unpaid statements summary
    const { loading: loadingUnpaid, data: unpaidData } = useQuery(GET_UNPAID_STATEMENTS_SUMMARY);

    useEffect(() => {
        refetchMetrics({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            status: selectedStatus === 'ALL' ? null : selectedStatus
        });
    }, [dateRange, selectedStatus, refetchMetrics]);

    const activeStatusLabel = STATUS_PILLS.find((p) => p.key === selectedStatus)?.label || 'All';
    const dealsCardLabel = selectedStatus === 'ALL' ? 'Total Deals' : `${activeStatusLabel} Deals`;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDING: 'bg-yellow-100 text-yellow-800',
            CONCLUDED: 'bg-blue-100 text-blue-800',
            COMPLETED: 'bg-green-100 text-green-800',
            FAILED: 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const pipelineMetrics = metricsData?.pipelineMetrics;
    const dealsRequiringAttention = attentionData?.dealsRequiringAttention || [];
    const serviceStatistics = servicesData?.professionalServiceStatistics;
    const unpaidSummary = unpaidData?.unpaidStatementsSummary;

    const isLoading = loadingMetrics || loadingAttention || loadingServices || loadingUnpaid;

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-32 bg-gray-300 rounded-lg"></div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-64 bg-gray-300 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold text-gray-900">Sales Pipeline Dashboard</h1>
                    <p className="text-gray-600">Monitor your sales performance and pipeline metrics</p>
                </div>

                {/* Date Range Selector */}
                <div className="flex items-center space-x-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Quick status pills — same as Sales Deals list */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                {STATUS_PILLS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        onClick={() => setSelectedStatus(p.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                            selectedStatus === p.key
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {/* Pipeline Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {/* Total Pipeline Value */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-blue-100">Total Pipeline Value</p>
                            <p className="text-2xl font-bold">
                                {formatCurrency(pipelineMetrics?.overallStats?.totalValue)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Active Deals */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-green-100">{dealsCardLabel}</p>
                            <p className="text-2xl font-bold">
                                {pipelineMetrics?.overallStats?.totalDeals || 0}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Average Deal Value */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-purple-100">Average Deal Value</p>
                            <p className="text-2xl font-bold">
                                {formatCurrency(pipelineMetrics?.overallStats?.averageDealValue)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Total Commission */}
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m2-10v18a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-yellow-100">Total Commission (excl. VAT)</p>
                            <p className="text-2xl font-bold">
                                {formatCurrency(pipelineMetrics?.overallStats?.totalCommission)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pipeline Status Breakdown */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Pipeline Status Breakdown</h3>
                            <p className="mt-1 text-sm text-gray-500">Current deals by status</p>
                        </div>
                        <div className="p-6">
                            {pipelineMetrics?.statusCounts && pipelineMetrics.statusCounts.length > 0 ? (
                                <div className="space-y-4">
                                    {pipelineMetrics.statusCounts.map((status) => (
                                        <div key={status.status} className="flex items-center justify-between p-4 rounded-lg border">
                                            <div className="flex items-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
                                                    {status.status}
                                                </span>
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-gray-900">{status.count} Deals</p>
                                                    <p className="text-xs text-gray-500">{formatCurrency(status.totalValue)}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {formatCurrency(status.totalCommission)}
                                                </p>
                                                <p className="text-xs text-gray-500">Commission</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-center py-8">No pipeline data available for selected period</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-6">
                    {/* Actions Card */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
                        </div>
                        <div className="p-6 space-y-3">
                            <Link 
                                to="/sales/deals/add" 
                                className="block w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded text-center transition duration-150 ease-in-out"
                            >
                                Add New Deal
                            </Link>
                            <Link 
                                to="/sales/professional-services" 
                                className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out"
                            >
                                Manage Services
                            </Link>
                            <Link 
                                to="/sales/commission-statements" 
                                className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out"
                            >
                                Commission Reports
                            </Link>
                            <Link 
                                to="/sales/deals" 
                                className="block w-full py-2 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-medium rounded text-center transition duration-150 ease-in-out"
                            >
                                View All Deals
                            </Link>
                        </div>
                    </div>

                    {/* Commission Summary */}
                    {unpaidSummary && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="p-6 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900">Commission Summary</h3>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Unpaid Statements</span>
                                        <span className="text-sm font-medium text-gray-900">{unpaidSummary.count}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Outstanding Amount</span>
                                        <span className="text-sm font-medium text-red-600">{formatCurrency(unpaidSummary.totalOutstanding)}</span>
                                    </div>
                                    {unpaidSummary.oldestStatementDate && (
                                        <div className="flex justify-between">
                                            <span className="text-sm text-gray-600">Oldest Statement</span>
                                            <span className="text-sm font-medium text-gray-900">{formatDate(unpaidSummary.oldestStatementDate)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                {/* Deals Requiring Attention */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900">Deals Requiring Attention</h3>
                            <Link to="/sales/deals" className="text-blue-600 hover:underline text-sm">View All</Link>
                        </div>
                        {dealsRequiringAttention.length > 0 && (
                            <p className="mt-1 text-sm text-red-600">{dealsRequiringAttention.length} deals need attention</p>
                        )}
                    </div>
                    <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                        {dealsRequiringAttention.length > 0 ? (
                            dealsRequiringAttention.slice(0, 5).map((deal) => (
                                <div key={deal.id} className="p-4 hover:bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/sales/deals/${deal.id}`}
                                                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                                >
                                                    {deal.dealNumber}
                                                </Link>
                                                {deal.sentToSmartTrack && (
                                                    <span
                                                        title="Sent to Smart Track"
                                                        aria-label="Sent to Smart Track"
                                                        className="inline-block w-2.5 h-2.5 rounded-full"
                                                        style={{
                                                            backgroundColor: '#39ff14',
                                                            boxShadow: '0 0 6px #39ff14, 0 0 2px #39ff14'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 truncate">{deal.streetAddress}</p>
                                            <div className="flex items-center mt-1 space-x-2">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(deal.status)}`}>
                                                    {deal.status}
                                                </span>
                                                {!deal.ficaChecked && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                        FICA Pending
                                                    </span>
                                                )}
                                                {!deal.depositReceived && deal.depositDueDate && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Deposit Due
                                                    </span>
                                                )}
                                                {deal.paymentMethod === 'BOND' && !deal.bondGranted && deal.bondGrantDate && (
                                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Bond Due
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 text-right">
                                            {formatDate(deal.dateCreated)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="p-6 text-gray-500 text-center">No deals requiring immediate attention</p>
                        )}
                    </div>
                </div>

                {/* Top Agent Performance */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Top Agent Performance</h3>
                        <p className="mt-1 text-sm text-gray-500">Based on commission earned</p>
                    </div>
                    <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                        {pipelineMetrics?.agentPerformance && pipelineMetrics.agentPerformance.length > 0 ? (
                            pipelineMetrics.agentPerformance.slice(0, 5).map((agent, index) => (
                                <div key={agent.agentId.id} className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                                index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                                index === 1 ? 'bg-gray-100 text-gray-800' :
                                                index === 2 ? 'bg-orange-100 text-orange-800' :
                                                'bg-blue-100 text-blue-800'
                                            }`}>
                                                #{index + 1}
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {agent.agentId.firstName} {agent.agentId.lastName}
                                                </p>
                                                <p className="text-xs text-gray-500">{agent.deals} deals</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-medium text-gray-900">
                                                {formatCurrency(agent.totalCommission)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formatCurrency(agent.averageDealValue)} avg
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="p-6 text-gray-500 text-center">No agent performance data available</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Professional Services Overview */}
            {serviceStatistics && (
                <div className="mt-8">
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Professional Services Overview</h3>
                            <p className="mt-1 text-sm text-gray-500">Service provider statistics</p>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-gray-900">{serviceStatistics.overallStats?.totalServices || 0}</p>
                                    <p className="text-sm text-gray-500">Total Services</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-green-600">
                                        {serviceStatistics.overallStats?.averageRating ? serviceStatistics.overallStats.averageRating.toFixed(1) : '0.0'}
                                    </p>
                                    <p className="text-sm text-gray-500">Average Rating</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-blue-600">
                                        {serviceStatistics.overallStats?.averageCompletionRate ? 
                                            (serviceStatistics.overallStats.averageCompletionRate * 100).toFixed(0) + '%' : '0%'}
                                    </p>
                                    <p className="text-sm text-gray-500">Completion Rate</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-purple-600">{serviceStatistics.overallStats?.totalDealsCompleted || 0}</p>
                                    <p className="text-sm text-gray-500">Deals Completed</p>
                                </div>
                            </div>

                            {/* Service Type Distribution */}
                            {serviceStatistics.typeDistribution && serviceStatistics.typeDistribution.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Service Types</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {serviceStatistics.typeDistribution.slice(0, 4).map((type) => (
                                            <div key={type.type} className="text-center p-3 border rounded-lg">
                                                <p className="text-lg font-semibold text-gray-900">{type.count}</p>
                                                <p className="text-xs text-gray-600">{type.type.replace('_', ' ')}</p>
                                                {type.averageRating && (
                                                    <p className="text-xs text-green-600">★ {type.averageRating.toFixed(1)}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Top Performers */}
                            {serviceStatistics.topPerformers && serviceStatistics.topPerformers.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Top Performing Services</h4>
                                    <div className="space-y-2">
                                        {serviceStatistics.topPerformers.slice(0, 3).map((service) => (
                                            <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{service.companyName}</p>
                                                    <p className="text-xs text-gray-500">{service.type}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-medium text-green-600">
                                                        ★ {service.performance.clientSatisfaction.toFixed(1)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {service.performance.dealsCompleted} deals
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesPipelineDashboard;